import { type AxiosError, isAxiosError } from 'axios'
import React from 'react'
import { Link } from 'react-router-dom'

import { getStatusChecksUrls } from 'components/status-checks/status-checks-urls-utils'
import { type AuthenticationResponse } from 'context/status-checks/ObservabilityToolAuthenticationContext'
import toast from 'lib/toast'
import { type RunTestEndpointResponse } from 'queries/external-integrations/types'
import { getMessageFromResponseOrError } from 'utils/strings'
import { isValidStatusCodeOrRange } from 'utils/team-external-integrations/team-external-integrations-util'

const MAX_RETRY_ATTEMPTS = 6
const STATUS_CHECK_URLS = getStatusChecksUrls()

export function getTestTeamExternalIntegrationEndpointConfigurationErrorMessage(
  httpStatusCode?: number,
  isPrivateNetworkIntegration?: boolean
) {
  if (!isValidStatusCodeOrRange(httpStatusCode)) {
    return `Authentication failed with status code ${httpStatusCode}.`
  }

  if (isPrivateNetworkIntegration) {
    return 'We were unable to get a result from testing the endpoint provided in the PNI authentication. Health Checks created using this authentication may not work as expected.'
  }

  return 'There was an error testing the endpoint provided. Please try again later.'
}

/**
 * Determines whether the run-test-endpoint mutation should continue to retest the integration's endpoint.
 * We expect our hook wrapper to handle 204 (empty response) as an error such that we can handle it within
 * react-query's retry logic. At some point we should stop after MAX_RETRY_ATTEMPTS.
 */
export function getRetryPredicate(failureCount: number, error: unknown) {
  if (failureCount >= MAX_RETRY_ATTEMPTS) return false

  return isAxiosError(error) && error.cause === 'No Content'
}

export function invokeToastOnSuccess() {
  toast({
    type: 'success',
    text: (
      <>
        Authentication has been saved to your team integration. It can be edited
        on the{' '}
        <Link to={STATUS_CHECK_URLS.authentication()}>
          Health Checks Authentication
        </Link>{' '}
        page.
      </>
    )
  })
}

export function invokeToastOnUnknownError(error: unknown) {
  toast({
    type: 'error',
    text: getMessageFromResponseOrError(
      error,
      'There was an error while saving your Team Authentication. Please try again later.'
    )
  })
}

/**
 * Given an error is in the expected shape of a `RunTestEndpointResponse` error, return a function that
 * can be used to set the authentication response state with the appropriate error message and details.
 */
export function setAuthenticationErrorResponse(
  error: AxiosError<RunTestEndpointResponse>,
  isPrivateNetworkIntegration?: boolean
) {
  return function (
    previousState: AuthenticationResponse
  ): AuthenticationResponse {
    const { testEndpointResponse } = error.response?.data || {}
    const { httpStatusCode, responseBody } = testEndpointResponse || {}

    return {
      ...previousState,
      alertSeverity: 'error',
      httpStatusCode,
      responseBody,
      message:
        error.message ??
        getTestTeamExternalIntegrationEndpointConfigurationErrorMessage(
          error.response?.status,
          isPrivateNetworkIntegration
        )
    }
  }
}

/**
 * Ensure that an error received within a catch block meets all the criteria necessary to determine
 * that the details of the error will conform to the expected shape of a `RunTestEndpointResponse` error.
 */
export function shouldDisplayAuthenticationErrorNotification(
  error: unknown
): error is AxiosError<RunTestEndpointResponse> {
  if (
    !error ||
    !isAxiosError(error) ||
    !isValidStatusCodeOrRange(error.status) ||
    !error?.response
  ) {
    return false
  }

  const responseData = error.response.data

  if (typeof responseData !== 'object' || responseData === null) {
    return false
  }

  return Boolean(error.message) || 'testEndpointResponse' in responseData
}
